import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ApiError, api, type Guia } from "@/lib/api";
import { copiarTexto } from "@/lib/copiar";
import { useAuth, isAdmin } from "@/lib/auth";
import GuiaEditorModal from "@/components/GuiaEditorModal";
import ConfirmDialog from "@/ui/ConfirmDialog";
import AyudaVista from "@/ui/AyudaVista";
import { ayudaGuiaDetalle } from "@/components/ayuda/vistas/guias";
import { useToast } from "@/ui/Toast";
import { slugifyHeading } from "@/lib/slug";
import ScrollToTopButton from "@/ui/ScrollToTopButton";
import VisorArchivo from "@/ui/VisorArchivo";

/** Vista de una guia (articulo de la base de conocimiento). */
function fechaLocal(iso: string): string {
  return new Date(iso).toLocaleDateString("es-MX", {
    day: "2-digit",
    month: "long",
    year: "numeric",
  });
}

export default function GuiaDetalle() {
  const { id } = useParams<{ id: string }>();
  const me = useAuth((s) => s.user);
  const toast = useToast();
  const nav = useNavigate();
  const [guia, setGuia] = useState<Guia | null>(null);
  const [loading, setLoading] = useState(true);
  const [editorOpen, setEditorOpen] = useState(false);
  // Ver el PDF de la guía en el visor antes de descargarlo.
  const [verPdf, setVerPdf] = useState(false);
  const [confirmDel, setConfirmDel] = useState(false);
  const contenidoRef = useRef<HTMLDivElement>(null);

  /** Intercepta clicks en <a href="#slug"> dentro del contenido para
   * que hagan scrollIntoView del heading correspondiente, en vez de
   * dejar que el browser intente navegar (en SPA el router se mete y
   * rompe). Esto hace funcionar los TOC/indices del .md importado. */
  function onContenidoClick(e: React.MouseEvent<HTMLDivElement>) {
    const target = e.target as HTMLElement;
    // 1) Botón "Copiar" inyectado en las celdas de comando.
    const btnCopiar = target.closest(".btn-copiar-cmd") as HTMLElement | null;
    if (btnCopiar) {
      e.preventDefault();
      const cmd = btnCopiar.dataset.cmd || "";
      if (!cmd) return;
      // `copiarTexto` trae el respaldo de <textarea> + execCommand: algunos
      // WebView de celular no exponen navigator.clipboard y aquí se quedaba
      // en «no pude copiar» sin más.
      const prev = btnCopiar.textContent || "⧉";
      void copiarTexto(cmd).then((ok) => {
        if (!ok) {
          toast.error("No pude copiar");
          return;
        }
        toast.success(btnCopiar.dataset.etiqueta || "Comando copiado");
        btnCopiar.textContent = "✓";
        btnCopiar.classList.add("copiado");
        setTimeout(() => {
          btnCopiar.textContent = prev;
          btnCopiar.classList.remove("copiado");
        }, 1200);
      });
      return;
    }
    // 2) Navegación del índice (TOC): <a href="#slug"> → scroll suave.
    const anchor = target.closest("a");
    if (!anchor) return;
    const href = anchor.getAttribute("href") || "";
    if (!href.startsWith("#")) return;
    const slug = href.slice(1);
    if (!slug) return;
    const root = contenidoRef.current;
    if (!root) return;
    // Buscar por id directamente — los headings tienen id="slug" gracias
    // al renderer de marked.
    const dest = root.querySelector(`#${CSS.escape(slug)}`);
    if (dest) {
      e.preventDefault();
      dest.scrollIntoView({ behavior: "smooth", block: "start" });
      // Resaltar brevemente para que el user vea a donde llego.
      (dest as HTMLElement).style.transition = "background 0.4s";
      (dest as HTMLElement).style.background = "rgba(20, 184, 166, 0.15)";
      setTimeout(() => {
        (dest as HTMLElement).style.background = "";
      }, 900);
    }
  }

  async function load() {
    if (!id) return;
    setLoading(true);
    try {
      setGuia(await api.get<Guia>(`/api/guias/${id}`));
    } catch (err) {
      const msg = err instanceof ApiError ? err.detail : "Error";
      toast.error("No pude cargar la guía", msg);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Re-deriva el id de cada heading desde su texto si no lo trae. Las
  // guias guardadas ANTES de que el editor persistiera el id en el schema
  // no tienen id en sus <h2>/<h3>, asi que los enlaces del indice/TOC no
  // encontraban destino. Esto los repara en la vista sin re-guardar. Usa
  // el MISMO slugify que el editor para que los hrefs `#slug` casen.
  useEffect(() => {
    const root = contenidoRef.current;
    if (!root) return;
    root.querySelectorAll("h1, h2, h3, h4, h5, h6").forEach((h) => {
      if (h.id) return;
      const slug = slugifyHeading(h.textContent || "");
      if (slug) h.id = slug;
    });
  }, [guia?.contenido_html]);

  // Inyecta un botón "Copiar" en cada celda (td) de las columnas marcadas
  // como comando (su encabezado u otra celda lleva class="celda-comando").
  // El botón se crea en el cliente y NO se guarda en el HTML → el PDF (que
  // renderiza el HTML guardado) sale con el comando como texto, sin botón.
  // Tablas con celdas combinadas (colspan) no están contempladas: las de
  // comandos son rejillas simples.
  useEffect(() => {
    const root = contenidoRef.current;
    if (!root) return;
    root.querySelectorAll<HTMLTableElement>("table").forEach((table) => {
      // Respeta los anchos de columna fijados en el editor: TipTap los guarda
      // en el atributo `colwidth` de las celdas, pero —a diferencia del
      // editor— el visor no arma el <colgroup> que los aplica, así que la
      // tabla caía a columnas iguales (50/50). Lo reconstruimos para que la
      // vista se vea como quedó en el editor. Soporta colspan y la lista de
      // anchos (colwidth="206,120").
      // Anchos PROPORCIONALES al contenido (como Excel): se mide el ancho
      // natural de cada columna en UNA línea y se reparte por PORCENTAJE. La
      // columna del comando (la más larga) agarra el mayor % y las de versión
      // quedan angostas — NO iguales. Como es %, la tabla siempre cabe
      // (móvil/tablet/escritorio) sin scroll. La medición es una sola pasada
      // SÍNCRONA (el navegador no pinta estados intermedios) -> sin parpadeo.
      if (!table.querySelector("colgroup") && table.rows.length) {
        const ncol = table.rows[0].cells.length;
        const prevTL = table.style.tableLayout;
        table.style.tableLayout = "auto";
        table.classList.add("midiendo-anchos"); // CSS: nowrap en todas las celdas
        const anchos = new Array<number>(ncol).fill(0);
        Array.from(table.rows).forEach((row) => {
          Array.from(row.cells).forEach((cell, i) => {
            if (i < ncol) anchos[i] = Math.max(anchos[i], cell.offsetWidth);
          });
        });
        table.classList.remove("midiendo-anchos");
        table.style.tableLayout = prevTL;
        const total = anchos.reduce((a, b) => a + b, 0) || 1;
        const colgroup = document.createElement("colgroup");
        anchos.forEach((w) => {
          const col = document.createElement("col");
          col.style.width = `${((w / total) * 100).toFixed(3)}%`;
          colgroup.appendChild(col);
        });
        table.insertBefore(colgroup, table.firstChild);
      }
      // TODA tabla de guía se prepara para el diseño responsivo. Antes esto
      // solo corría si había columna de comando; ahora aplica a cualquier tabla
      // para que ninguna se aplaste en pantalla angosta (se vuelve tarjetas).
      table.classList.add("tabla-guia");

      // Envuelve la tabla en un contenedor: en escritorio se ve como tabla; en
      // móvil deja de scrollear y la tabla se apila en tarjetas.
      if (!table.parentElement?.classList.contains("tabla-scroll")) {
        const wrap = document.createElement("div");
        wrap.className = "tabla-scroll";
        table.parentNode?.insertBefore(wrap, table);
        wrap.appendChild(table);
      }

      // Etiquetas de columna (del encabezado) para las tarjetas en móvil: cada
      // celda muestra su etiqueta arriba.
      const filas = Array.from(table.rows);
      const filaEnc = filas.find((r) => r.querySelector("th")) || filas[0] || null;
      const etiquetas = filaEnc
        ? Array.from(filaEnc.cells).map((c) => (c.innerText || "").trim())
        : [];
      if (filaEnc) filaEnc.classList.add("fila-encabezado");
      table.querySelectorAll<HTMLTableRowElement>("tr").forEach((row) => {
        if (row === filaEnc) return;
        Array.from(row.cells).forEach((cell, i) => {
          if (etiquetas[i]) cell.setAttribute("data-col", etiquetas[i]);
        });
      });

      // Extras SOLO para columnas de comando: botón "Copiar" por celda.
      const columnas = new Set<number>();
      table
        .querySelectorAll<HTMLTableCellElement>(
          "th.celda-comando, td.celda-comando",
        )
        .forEach((c) => columnas.add(c.cellIndex));
      if (columnas.size === 0) return;
      table.querySelectorAll<HTMLTableRowElement>("tr").forEach((row) => {
        if (row === filaEnc) return;
        columnas.forEach((idx) => {
          const cell = row.cells[idx];
          if (!cell || cell.tagName === "TH") return;
          if (cell.querySelector(".btn-copiar-cmd")) return;
          const comando = (cell.innerText || "").trim();
          if (!comando) return;
          cell.classList.add("celda-comando");
          const btn = document.createElement("button");
          btn.type = "button";
          btn.className = "btn-copiar-cmd";
          // Sin `title` (cero tooltips): qué hace el ⧉ lo dice la Ayuda.
          btn.setAttribute("aria-label", "Copiar comando");
          btn.dataset.cmd = comando;
          btn.textContent = "⧉";
          cell.insertBefore(btn, cell.firstChild);
        });
      });

      // Y uno para llevarse TODA la columna de un clic: en el índice de
      // firmware, cada tabla es una versión, así que esto copia todas sus
      // ligas listas para pegar en un correo (pedido de Joe, 2026-08-11).
      // Reusa la misma clase que los botones por celda → mismo handler.
      columnas.forEach((idx) => {
        const th = filaEnc?.cells[idx];
        if (!th || th.querySelector(".btn-copiar-cmd")) return;
        const lineas: string[] = [];
        table.querySelectorAll<HTMLTableRowElement>("tr").forEach((row) => {
          if (row === filaEnc) return;
          // El valor limpio sale del botón de la celda, NO de innerText: para
          // entonces la celda ya tiene inyectado el ⧉ y se colaba en el texto.
          const celda = row.cells[idx];
          const propio = celda?.querySelector<HTMLElement>(".btn-copiar-cmd");
          const valor = (propio?.dataset.cmd || celda?.innerText || "").trim();
          if (!valor) return;
          // Se antepone el nombre del archivo (1ª columna): pegar solo URLs
          // en un correo no le dice nada a quien lo recibe.
          const etiqueta = (row.cells[0]?.innerText || "").trim();
          lineas.push(etiqueta && idx !== 0 ? `${etiqueta}: ${valor}` : valor);
        });
        if (lineas.length < 2) return;  // con una sola, el botón de celda basta
        const btn = document.createElement("button");
        btn.type = "button";
        btn.className = "btn-copiar-cmd btn-copiar-todas";
        btn.setAttribute("aria-label", `Copiar las ${lineas.length} ligas de este bloque`);
        btn.dataset.cmd = lineas.join("\n");
        btn.dataset.etiqueta = `${lineas.length} ligas copiadas`;
        btn.textContent = `⧉ copiar las ${lineas.length}`;
        // Va DESPUÉS del texto y sin partirse: puesto antes, el encabezado se
        // angostaba y «todas» salía una letra por renglón.
        btn.style.whiteSpace = "nowrap";
        btn.style.marginLeft = "8px";
        th.appendChild(btn);

        // En el celular la tabla se vuelve tarjetas y la fila de encabezado se
        // OCULTA, así que este botón existía pero no se veía (medido: 140 en la
        // guía de firmware, 0 visibles a 412px). Copia del mismo botón en una
        // barra arriba de la tabla, que CSS muestra solo en móvil.
        const contenedor = table.closest(".tabla-scroll") ?? table;
        const previo = contenedor.previousElementSibling;
        const barra =
          previo?.classList.contains("barra-copiar-tabla")
            ? (previo as HTMLElement)
            : Object.assign(document.createElement("div"), { className: "barra-copiar-tabla" });
        if (barra !== previo) contenedor.parentElement?.insertBefore(barra, contenedor);
        const enBarra = btn.cloneNode(true) as HTMLElement;
        enBarra.style.marginLeft = "0";
        // En la barra ya no está el encabezado al lado, así que el botón dice de
        // QUÉ columna copia («⧉ copiar las 3 (Liga)»). Entre paréntesis y con el
        // nombre tal cual: sirve para cualquier columna sin inventar plurales.
        const nombreCol = (th.textContent || "").replace(/⧉.*$/, "").trim();
        if (nombreCol) enBarra.textContent = `⧉ copiar las ${lineas.length} (${nombreCol})`;
        barra.appendChild(enBarra);
      });
    });
  }, [guia?.contenido_html]);

  async function eliminar() {
    if (!guia) return;
    try {
      await api.del(`/api/guias/${guia.id}`);
      toast.success("Guía eliminada");
      nav("/guias");
    } catch (err) {
      const msg = err instanceof ApiError ? err.detail : "Error";
      toast.error("No pude eliminar", msg);
    }
  }

  if (loading) {
    return <p className="text-sm text-zinc-500">Cargando guía…</p>;
  }
  if (!guia) {
    return (
      <div className="space-y-3">
        <p className="text-sm text-zinc-500">La guía no existe.</p>
        <Link to="/guias" className="text-accent-400 text-sm">← Guías</Link>
      </div>
    );
  }

  return (
    <div className="space-y-4 max-w-[1500px] mx-auto">
      <Link to="/guias" className="text-accent-400 text-sm">← Guías</Link>

      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0">
          <div className="flex items-start gap-2">
            <h1 className="min-w-0 text-xl sm:text-2xl font-semibold break-words">{guia.titulo}</h1>
            <AyudaVista
              titulo="Ver una guía"
              className="mt-0.5"
              {...ayudaGuiaDetalle({ puedeEditar: isAdmin(me) || me?.id === guia.autor_id })}
            />
          </div>
          {guia.tags.length > 0 && (
            <div className="flex flex-wrap gap-1 mt-1.5">
              {guia.tags.map((t) => (
                <span
                  key={t}
                  className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded border border-ink-700 bg-ink-800/40 text-zinc-400"
                >
                  {t}
                </span>
              ))}
            </div>
          )}
          <p className="text-xs text-zinc-500 mt-1.5">
            {guia.autor_nombre || "—"} · actualizada{" "}
            {new Date(guia.updated_at).toLocaleString()}
            {guia.estado === "borrador" && (
              <span className="ml-2 text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded border border-amber-500/30 bg-amber-500/10 text-amber-300">
                borrador
              </span>
            )}
          </p>
          {guia.fuente === "queclink-kb" && (
            /* Lo que importa avisar no es de dónde vino —los tags ya lo dicen—
               sino QUÉ PASA SI LA EDITAS: no se pisa, la actualización de
               Queclink llega aparte. Sin decirlo, uno no la toca por miedo. */
            <p className="text-[11px] text-zinc-500 mt-1">
              📘 Traída de la base de conocimiento de Queclink
              {guia.fuente_actualizado_at && (
                <> · allá se actualizó el {fechaLocal(guia.fuente_actualizado_at)}</>
              )}
              . Puedes editarla con confianza:{" "}
              <strong className="text-zinc-400">no se sobrescribe</strong> — si Queclink la
              cambia, la versión nueva llega como una guía aparte en borrador.
            </p>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-2 sm:flex-shrink-0">
          <button
            type="button"
            onClick={() => setVerPdf(true)}
            className="btn-ghost text-sm"
          >
            👁 Ver PDF
          </button>
          <a
            href={`/api/guias/${guia.id}/pdf`}
            className="btn-ghost text-sm"
          >
            ↓ PDF
          </a>
          {verPdf && (
            <VisorArchivo
              targets={[
                {
                  nombre: `${guia.titulo}.pdf`,
                  mime: "application/pdf",
                  url: `/api/guias/${guia.id}/pdf`,
                },
              ]}
              onClose={() => setVerPdf(false)}
            />
          )}
          {(isAdmin(me) || me?.id === guia.autor_id) && (
            <>
              <button onClick={() => setEditorOpen(true)} className="btn-ghost text-sm">
                Editar
              </button>
              <button
                onClick={() => setConfirmDel(true)}
                className="btn-ghost text-sm text-red-400 hover:text-red-300"
              >
                Eliminar
              </button>
            </>
          )}
        </div>
      </div>

      {guia.contenido_html?.trim() ? (
        <div
          ref={contenidoRef}
          className="card tiptap-content text-sm text-zinc-200"
          dangerouslySetInnerHTML={{ __html: guia.contenido_html }}
          onClick={onContenidoClick}
        />
      ) : (
        <p className="text-sm text-zinc-500">Esta guía no tiene contenido todavía.</p>
      )}


      <GuiaEditorModal
        open={editorOpen}
        onClose={() => setEditorOpen(false)}
        guia={guia}
        onSaved={(g) => setGuia(g)}
      />
      <ConfirmDialog
        open={confirmDel}
        onOpenChange={setConfirmDel}
        title="Eliminar guía"
        description={`La guía "${guia.titulo}" se borra permanentemente.`}
        variant="danger"
        confirmLabel="Eliminar"
        onConfirm={eliminar}
      />

      <ScrollToTopButton />
    </div>
  );
}
